import { NgFor, NgIf } from '@angular/common';
import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
	IconComponent,
	SkeletonLoaderComponent,
	TimesPipe,
	TypographyComponent
} from 'components';
import { Observable, of, Subject } from 'rxjs';
import {
	catchError,
	debounceTime,
	filter,
	map,
	switchMap,
	takeUntil
} from 'rxjs/operators';

import {
	AnaActionRouterService,
	isNavigationAction
} from '@brand-lolly/app/_services/ana-action-router.service';
import { AnaAssistantFacadeService } from '@brand-lolly/app/_services/ana-assistant-facade.service';
import {
	ProposalItem,
	ProposalLink
} from '@brand-lolly/app/_services/notifications/notification.model';
import { NotificationsService } from '@brand-lolly/app/_services/notifications/notifications.service';
import { SETTING_QUERY_PARAM } from '@brand-lolly/app/_services/ana/ana-settings-reveal.service';
import { isNavigationEntryShown } from '@brand-lolly/app/shared/navigation/navigation-entry-visibility';

import { UniversalSearchApiService } from './universal-search-api.service';
import {
	HighlightedPart,
	highlight,
	matchPages,
	MAX_SETTINGS,
	rankSettings,
	searchablePages,
	searchableSettings,
	searchWords,
	settingAddress
} from './universal-search-pages';
import {
	UniversalSearchKindResult,
	UniversalSearchPage,
	UniversalSearchRecordKind,
	UniversalSearchSetting
} from './universal-search.model';
import { AFFILIATES_CAMPAIGNS_PATH } from '@brand-lolly/app/social-media-influencer/modules/affiliates/models/affiliates-campaigns-path.model';

/** `'followed'` when the search led somewhere, so focus is not handed back. */
export type UniversalSearchResult = 'followed' | undefined;

export interface UniversalSearchData {
	/** The rail's search box, hidden while the search is open. */
	readonly anchor: HTMLElement | null;
	/** Set when the box is on screen: the panel grows out of it and flies back. */
	readonly morph: HTMLElement | null;
}

// CONTRACT: the iOS drawer curve emil-design-eng names for a surface moving into place.
const MORPH_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const MORPH_IN_MS = 320;
const MORPH_OUT_MS = 220;
// The window in which the box's copy dissolves into the panel's own content.
const CROSSFADE_MS = 140;
// How long the box's copy takes to dissolve on the way out of the rail.
const GHOST_OUT_MS = 100;
const CONTENT_OUT_MS = 90;
// CONTRACT: the class UniversalSearchService puts on the rail box while it is lifted out.
const LIFTED = 'rail-search--lifted';

interface SearchRow {
	readonly domId: string;
	readonly icon: string;
	/** Where it lives, drawn before the name: "Affiliates ›". */
	readonly prefix: string | null;
	readonly parts: HighlightedPart[];
	readonly caption: string | null;
	/** Drawn after the caption and never truncated: "Ana asks which campaign". */
	readonly handOff: string | null;
	readonly follow: () => void;
}

interface SearchGroup {
	readonly key: string;
	readonly title: string;
	readonly rows: SearchRow[];
	readonly notice: string | null;
}

interface RecordAnswer {
	readonly query: string;
	readonly kinds: UniversalSearchKindResult[] | null;
}

const KIND_TITLES: Record<UniversalSearchRecordKind, string> = {
	campaigns: 'Campaigns',
	programmes: 'Affiliate campaigns',
	affiliates: 'Affiliates',
	automations: 'Automations',
	gift_campaigns: 'Gift sends',
	conversations: 'Conversations'
};

const KIND_ICONS: Record<UniversalSearchRecordKind, string> = {
	campaigns: 'nav_ai_projects',
	programmes: 'nav_affiliates',
	affiliates: 'members',
	automations: 'nav_ai_agent',
	gift_campaigns: 'gift',
	conversations: 'nav_messaging'
};

// CONTRACT: each module list reads `?search=` into its own search field on load.
// The automations list has no search field, so it gets no "All … matching" row.
const KIND_LISTS: Partial<Record<UniversalSearchRecordKind, string>> = {
	campaigns: '/campaigns',
	programmes: `/affiliates/${AFFILIATES_CAMPAIGNS_PATH}`,
	affiliates: '/affiliates/affiliates',
	gift_campaigns: '/affiliates/gifting',
	conversations: '/messaging/inbox'
};

// CONTRACT: the rail's own icon per department label (sidenav-group.component.ts
// `iconAlias`), so a page result carries the mark of the section it lives in.
const DEPARTMENT_ICONS: Record<string, string> = {
	Home: 'nav_home',
	Campaigns: 'nav_ai_projects',
	Content: 'nav_lists',
	Discovery: 'nav_ai_agent',
	Messaging: 'nav_messaging',
	'Partner Ads': 'meta_mentions',
	'Social Listening': 'nav_social_listening',
	Affiliates: 'nav_affiliates',
	'Chrome Extension': 'nav_advanced_search',
	Autopilot: 'nav_messaging',
	Account: 'navbar_profile'
};

// CONTRACT: the rail's profile menu marks Settings with this gear.
const SETTING_ICON = 'regular_settings';

const MIN_RECORD_QUERY = 2;

// CONTRACT: Home's hub carries tray rules from every department (backend hubs.py), so its scope is the widest set there is.
const PROPOSALS_SCOPE = '/';
const MAX_PROPOSALS = 3;

const RULE_DEPARTMENTS: Record<string, string> = {
	relationship: 'Messaging',
	messaging: 'Messaging',
	social_listening: 'Social Listening',
	affiliate: 'Affiliates',
	partner_ads: 'Partner Ads'
};

function destinationOf(item: ProposalItem): ProposalLink | null {
	const link = item.evidence ?? item.action;
	if (!link) return null;
	// TRAP: only navigations — connect_*, buy_credits, messaging_* open a flow or change something, which the search never does.
	return isNavigationAction(link.action_kind) ? link : null;
}

const OFF_WORDS = ['off', 'stop', 'pause', 'disable', 'unsubscribe'];
const ON_WORDS = ['on', 'start', 'resume', 'enable'];

/**
 * What a hand-off row sends Ana: the setting and the record it needs —
 * "Turn off Follow-up Agent — which campaign?". "Turn off" / "Turn on" only
 * for a switch and only when the query says so; otherwise "Change".
 */
function handOffQuestion(
	setting: UniversalSearchSetting,
	query: string
): string {
	const words = searchWords(query);
	const off = words.some(word => OFF_WORDS.includes(word));
	const on = words.some(word => ON_WORDS.includes(word));
	const verb =
		setting.writable && off !== on
			? off
				? 'Turn off'
				: 'Turn on'
			: 'Change';
	return `${verb} ${setting.label} — which ${setting.recordNoun}?`;
}

/** "programs, affiliates and gift campaigns", capitalised for a sentence start. */
function listed(names: string[]): string {
	const text =
		names.length > 1
			? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
			: names[0] ?? '';
	return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The universal search: pages and the brand's own records, found by what the
 * brand types, each leading to its own page — and Ana, last, for anything that
 * needs doing. It never changes anything itself.
 */
@Component({
	selector: 'app-universal-search',
	standalone: true,
	imports: [
		NgFor,
		NgIf,
		ReactiveFormsModule,
		IconComponent,
		MatTooltipModule,
		SkeletonLoaderComponent,
		TimesPipe,
		TypographyComponent
	],
	templateUrl: './universal-search.component.html',
	styleUrls: ['./universal-search.component.scss']
})
export class UniversalSearchComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	@ViewChild('input') private input?: ElementRef<HTMLInputElement>;

	readonly query = new FormControl('', { nonNullable: true });
	readonly listboxId = 'universal-search-results';

	groups: SearchGroup[] = [];
	rows: SearchRow[] = [];
	activeIndex = 0;
	activeId: string | null = null;
	recordsLoading = false;

	private readonly pages: UniversalSearchPage[] = searchablePages();
	private readonly settings: UniversalSearchSetting[] = searchableSettings();
	private readonly listeningShown =
		isNavigationEntryShown('social-listening');
	private records: RecordAnswer | null = null;
	private proposals: ProposalItem[] = [];
	private closing = false;
	private ghost: HTMLElement | null = null;
	private readonly unsubscriber$ = new Subject<void>();

	constructor(
		private dialogRef: MatDialogRef<
			UniversalSearchComponent,
			UniversalSearchResult
		>,
		private api: UniversalSearchApiService,
		private router: Router,
		private anaFacade: AnaAssistantFacadeService,
		private notificationsService: NotificationsService,
		private actionRouter: AnaActionRouterService,
		private host: ElementRef<HTMLElement>,
		@Inject(MAT_DIALOG_DATA) private data: UniversalSearchData | null
	) {}

	ngOnInit(): void {
		// TRAP: no placeholder while this loads — the group is optional and usually small, and a skeleton that collapses to nothing is worse than a late arrival.
		this.notificationsService
			.getProposals(PROPOSALS_SCOPE)
			.pipe(
				map(response => response?.items ?? []),
				catchError(() => of([] as ProposalItem[])),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(items => {
				this.proposals = items;
				if (!this.query.value.trim()) this.rebuild();
			});

		// ORDER: clear the old answer BEFORE the debounce — an answer for "sum"
		// must not sit under "summer" while the next request is waiting.
		this.query.valueChanges
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(value => {
				const query = value.trim();
				this.recordsLoading = query.length >= MIN_RECORD_QUERY;
				if (this.records?.query !== query) this.records = null;
				this.rebuild();
			});

		this.query.valueChanges
			.pipe(
				map(value => value.trim()),
				// TRAP: no distinctUntilChanged — retyping the last answered query
				// must re-ask, or the loading flag set above is never cleared.
				debounceTime(200),
				switchMap(query => this.searchRecords(query)),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(answer => {
				if (answer.query !== this.query.value.trim()) return;
				this.records = answer;
				this.recordsLoading = false;
				// TRAP: an empty query's answer arrives after the debounce and would reset the active row the brand just moved in Needs you.
				if (!answer.query) return;
				this.rebuild();
			});
	}

	ngAfterViewInit(): void {
		this.input?.nativeElement.focus();
		// ORDER: measured after the overlay has positioned the pane — measured any
		// earlier, the panel's own box is not where it lands. Hidden until then, so
		// it never shows a frame in its final place before flying there.
		const host = this.host.nativeElement;
		host.style.opacity = '0';
		this.dialogRef
			.afterOpened()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => {
				host.style.opacity = '';
				this.morph('in');
			});
		// The dialog is disableClose, so a dismissal can fly back first.
		this.dialogRef
			.backdropClick()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => this.close());
		this.dialogRef
			.keydownEvents()
			.pipe(
				filter(event => event.key === 'Escape'),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(event => {
				event.preventDefault();
				this.close();
			});
	}

	ngOnDestroy(): void {
		this.dropGhost();
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	onKeydown(event: KeyboardEvent): void {
		if (!this.rows.length) return;
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			const step = event.key === 'ArrowDown' ? 1 : -1;
			this.setActive(
				(this.activeIndex + step + this.rows.length) % this.rows.length
			);
		} else if (event.key === 'Enter') {
			event.preventDefault();
			this.rows[this.activeIndex]?.follow();
		}
	}

	setActive(index: number): void {
		this.activeIndex = index;
		this.activeId = this.rows[index]?.domId ?? null;
		document
			.getElementById(this.activeId ?? '')
			?.scrollIntoView({ block: 'nearest' });
	}

	/** Dismissed: shrink back into the rail box, then go. */
	close(): void {
		if (this.closing) return;
		this.closing = true;
		const animation = this.morph('out');
		if (!animation) {
			this.dialogRef.close();
			return;
		}
		animation.finished.then(
			() => this.dialogRef.close(),
			() => this.dialogRef.close()
		);
	}

	/**
	 * The rail box becoming the panel, or the panel going back into it.
	 *
	 * The panel is drawn where it ends, then translated and clipped to the box's
	 * place and size, and the clip opens out (FLIP). A frozen copy of the box
	 * rides along on top and dissolves while the panel's own content appears,
	 * so the first frame is the box itself rather than a field that looks
	 * almost like it. The way back runs the same in reverse and lands the copy
	 * on the box's empty socket.
	 */
	private morph(direction: 'in' | 'out'): Animation | null {
		const anchor = this.data?.morph;
		const host = this.host.nativeElement;
		const backdrop = document.querySelector<HTMLElement>(
			'.universal-search-backdrop'
		);
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			return direction === 'in'
				? host.animate([{ opacity: 0 }, { opacity: 1 }], 120)
				: null;
		}
		if (!anchor) return null;
		const from = anchor.getBoundingClientRect();
		const to = host.getBoundingClientRect();
		const field =
			host.querySelector<HTMLElement>('.search-field')?.offsetHeight ?? 0;
		const inset = Math.max(0, (field - from.height) / 2);
		const collapsed = {
			transform: `translate(${from.left - to.left}px, ${
				from.top - to.top - inset
			}px)`,
			clipPath: `inset(${inset}px ${to.width - from.width}px ${
				to.height - inset - from.height
			}px 0px round 0.5rem)`
		};
		const open = {
			transform: 'translate(0px, 0px)',
			clipPath: 'inset(0px 0px 0px 0px round 1rem)'
		};
		const inward = direction === 'in';
		const duration = inward ? MORPH_IN_MS : MORPH_OUT_MS;
		const timing = { duration, easing: MORPH_EASE };

		backdrop?.animate(
			inward
				? [{ opacity: 0 }, { opacity: 1 }]
				: [{ opacity: 1 }, { opacity: 0 }],
			{ ...timing, fill: 'forwards' }
		);

		// The copy travels with the panel's top-left corner, centred on its field row.
		const ghost = this.ghostOf(anchor, from);
		const atRail = 'translate(0px, 0px)';
		const atPanel = `translate(${to.left - from.left}px, ${
			to.top + inset - from.top
		}px)`;
		ghost.animate(
			inward
				? [{ transform: atRail }, { transform: atPanel }]
				: [{ transform: atPanel }, { transform: atRail }],
			{ ...timing, fill: 'forwards' }
		);
		ghost.animate(
			inward
				? [{ opacity: 1 }, { opacity: 0 }]
				: [{ opacity: 0 }, { opacity: 1 }],
			{
				duration: inward ? GHOST_OUT_MS : CROSSFADE_MS,
				// ORDER: on the way back the panel's content is gone before the copy
				// shows, so the two lines of text never sit on top of each other.
				delay: inward ? 0 : CONTENT_OUT_MS + 10,
				easing: 'ease-out',
				fill: 'both'
			}
		);
		for (const part of Array.from(host.children) as HTMLElement[]) {
			part.animate(
				inward
					? [{ opacity: 0 }, { opacity: 1 }]
					: [{ opacity: 1 }, { opacity: 0 }],
				{
					duration: inward ? CROSSFADE_MS : CONTENT_OUT_MS,
					// ORDER: after the copy has gone, so the two never overlap.
					delay: inward ? GHOST_OUT_MS : 0,
					easing: 'ease-out',
					fill: inward ? 'backwards' : 'forwards'
				}
			);
		}

		// TRAP: no fill on the way in — a held clip-path would cut off the panel's
		// shadow for as long as it is open.
		const panel = host.animate(
			inward ? [collapsed, open] : [open, collapsed],
			inward ? timing : { ...timing, fill: 'forwards' }
		);
		if (inward) {
			panel.finished.then(
				() => this.dropGhost(),
				() => this.dropGhost()
			);
		}
		return panel;
	}

	/**
	 * A copy of the rail box, pinned over it above the overlay. Every computed
	 * style is written onto it, because the box's own styles are scoped to the
	 * rail and would not reach a copy outside it.
	 */
	private ghostOf(anchor: HTMLElement, rect: DOMRect): HTMLElement {
		this.dropGhost();
		// ORDER: copied as the box looks in the rail, not as the empty socket it
		// is while the search is open — the way back lands the box, not the socket.
		const lifted = anchor.classList.contains(LIFTED);
		anchor.classList.remove(LIFTED);
		const ghost = anchor.cloneNode(true) as HTMLElement;
		const sources = [anchor, ...Array.from(anchor.querySelectorAll('*'))];
		const copies = [ghost, ...Array.from(ghost.querySelectorAll('*'))];
		sources.forEach((source, index) => {
			const style = getComputedStyle(source);
			const copy = copies[index] as HTMLElement;
			for (const property of Array.from(style)) {
				copy.style.setProperty(
					property,
					style.getPropertyValue(property)
				);
			}
			copy.style.transition = 'none';
			copy.style.animation = 'none';
			copy.style.visibility = 'visible';
		});
		if (lifted) anchor.classList.add(LIFTED);
		Object.assign(ghost.style, {
			position: 'fixed',
			left: `${rect.left}px`,
			top: `${rect.top}px`,
			width: `${rect.width}px`,
			height: `${rect.height}px`,
			margin: '0',
			zIndex: '1001',
			pointerEvents: 'none',
			opacity: '1'
		});
		ghost.removeAttribute('id');
		ghost.setAttribute('aria-hidden', 'true');
		document.body.appendChild(ghost);
		this.ghost = ghost;
		return ghost;
	}

	private dropGhost(): void {
		this.ghost?.remove();
		this.ghost = null;
	}

	private searchRecords(query: string): Observable<RecordAnswer> {
		if (query.length < MIN_RECORD_QUERY) return of({ query, kinds: [] });
		return this.api.search(query).pipe(
			map(kinds => ({ query, kinds })),
			catchError(() => of({ query, kinds: null }))
		);
	}

	private navigate(url: string, queryParams?: Record<string, string>): void {
		this.dialogRef.close('followed');
		if (queryParams) {
			this.router.navigate([url], { queryParams });
		} else {
			this.router.navigateByUrl(url);
		}
	}

	// TRAP: no actioned report and no dismissal — the search never changes anything, so a proposal followed from here only goes where it leads.
	private followProposal(link: ProposalLink): void {
		this.dialogRef.close('followed');
		this.actionRouter.dispatchAction(
			link.action_kind,
			link.action_payload ?? undefined
		);
	}

	/** The setting's screen, with `?setting=<key>` added to whatever query it has. */
	private followSetting(key: string, address: string): void {
		const tree = this.router.parseUrl(address);
		tree.queryParams = {
			...tree.queryParams,
			[SETTING_QUERY_PARAM]: key
		};
		this.dialogRef.close('followed');
		this.router.navigateByUrl(tree);
	}

	/** Opens Ana with the query in her composer, not sent. */
	private askAna(query: string): void {
		this.dialogRef.close('followed');
		this.anaFacade.openWithDraft(query);
	}

	/** Opens Ana docked and SENDS her the question, so she asks which record. */
	private handOff(question: string): void {
		this.dialogRef.close('followed');
		this.anaFacade.openWithMessage(question, { expand: false });
	}

	private rebuild(): void {
		const query = this.query.value.trim();
		const groups: SearchGroup[] = [];
		let next = 0;
		const row = (
			icon: string,
			text: string,
			caption: string | null,
			follow: () => void,
			prefix: string | null = null,
			handOff: string | null = null
		): SearchRow => ({
			domId: `universal-search-row-${next++}`,
			icon,
			prefix,
			parts: highlight(text, query),
			caption,
			handOff,
			follow
		});

		if (!query) {
			const needsYou = this.proposals
				.map(item => ({ item, link: destinationOf(item) }))
				.filter(
					(
						candidate
					): candidate is {
						item: ProposalItem;
						link: ProposalLink;
					} =>
						!!candidate.link &&
						this.actionRouter.canDispatch(
							candidate.link.action_kind,
							candidate.link.action_payload ?? undefined
						)
				)
				.slice(0, MAX_PROPOSALS)
				.map(({ item, link }) => {
					const department =
						RULE_DEPARTMENTS[item.rule_id.split('.')[0]] ?? null;
					return row(
						(department && DEPARTMENT_ICONS[department]) ?? 'grid',
						item.title,
						null,
						() => this.followProposal(link),
						department ? `${department} ›` : null
					);
				});
			if (needsYou.length) {
				groups.push({
					key: 'needs-you',
					title: 'Needs you',
					notice: null,
					rows: needsYou
				});
			}
		}

		if (query) {
			const pages = matchPages(query, this.pages);
			if (pages.length) {
				groups.push({
					key: 'pages',
					title: 'Pages',
					notice: null,
					rows: pages.map(page =>
						row(
							DEPARTMENT_ICONS[page.department] ?? 'grid',
							page.title,
							null,
							() => this.navigate(page.address),
							`${page.department} ›`
						)
					)
				});
			}
			// ORDER: addresses resolved BEFORE ranking — the setting of the record
			// on screen is the one preferred among equals.
			const url = this.router.url;
			const addresses = new Map<UniversalSearchSetting, string | null>(
				this.settings.map(setting => [
					setting,
					settingAddress(setting, url)
				])
			);
			const matches = rankSettings(
				query,
				this.settings,
				setting =>
					(setting.recordTemplate !== null ||
						setting.listeningRecord) &&
					addresses.get(setting) !== null
			);
			if (matches.length) {
				const hidden = matches.length - MAX_SETTINGS;
				const rows = matches
					.slice(0, MAX_SETTINGS)
					.map(({ item, term }) => {
						const where = `${item.department} › ${item.screenTitle}`;
						const caption = term
							? `matches ‘${term}’ · ${where}`
							: where;
						const address = addresses.get(item) ?? null;
						// A record's setting with no record on screen: Ana asks which one.
						return address
							? row(SETTING_ICON, item.label, caption, () =>
									this.followSetting(item.key, address)
							  )
							: row(
									SETTING_ICON,
									item.label,
									caption,
									() =>
										this.handOff(
											handOffQuestion(item, query)
										),
									null,
									`Ana asks which ${item.recordNoun}`
							  );
					});
				// Never dropped silently: the rest are one row away, asked in Ana.
				if (hidden > 0) {
					rows.push(
						row(
							'ai_lolly_logo',
							`${hidden} more ${
								hidden === 1 ? 'setting' : 'settings'
							}`,
							null,
							() => this.handOff(query),
							null,
							'Ask Ana'
						)
					);
				}
				groups.push({
					key: 'settings',
					title: 'Settings',
					notice: null,
					rows
				});
			}
		}

		if (this.records?.kinds === null) {
			groups.push({
				key: 'records-failed',
				title: 'Records',
				notice: "Couldn't search your records right now",
				rows: []
			});
		}
		const kinds = this.records?.kinds ?? [];
		// One line for every kind refused for the same unpaid bill, not one each.
		const pastDue = kinds.filter(kind => kind.notSearched === 'past_due');
		for (const kind of kinds) {
			if (kind.notSearched === 'past_due') {
				if (kind !== pastDue[0]) continue;
				const names = pastDue.map(each =>
					KIND_TITLES[each.kind].toLowerCase()
				);
				groups.push({
					key: 'past-due',
					title: 'Affiliates',
					notice: `${listed(
						names
					)} not searched — the affiliate subscription is past due`,
					rows: []
				});
				continue;
			}
			if (kind.notSearched) {
				groups.push({
					key: kind.kind,
					title: KIND_TITLES[kind.kind],
					notice: `Couldn't search ${KIND_TITLES[
						kind.kind
					].toLowerCase()} right now`,
					rows: []
				});
				continue;
			}
			if (!kind.items.length) continue;
			const rows = kind.items.map(item =>
				row(KIND_ICONS[kind.kind], item.name, item.secondary, () =>
					this.navigate(item.route)
				)
			);
			const list = KIND_LISTS[kind.kind];
			if (kind.hasMore && list) {
				rows.push(
					row(
						'search',
						`All ${KIND_TITLES[
							kind.kind
						].toLowerCase()} matching “${query}”`,
						null,
						() => this.navigate(list, { search: query })
					)
				);
			}
			groups.push({
				key: kind.kind,
				title: KIND_TITLES[kind.kind],
				notice: null,
				rows
			});
		}

		if (query) {
			const elsewhere: SearchRow[] = [];
			if (this.listeningShown) {
				elsewhere.push(
					row(
						'nav_social_listening',
						`Mentions of “${query}”`,
						'Social Listening',
						() =>
							this.navigate('/social-listening/my-brand', {
								keyword: query
							})
					)
				);
			}
			elsewhere.push(
				row(
					'nav_discovery',
					`Find “${query}” in Discovery`,
					'AI Search',
					() => this.navigate('/ai-search', { q: query })
				)
			);
			groups.push({
				key: 'elsewhere',
				title: 'Search elsewhere',
				notice: null,
				rows: elsewhere
			});
			groups.push({
				key: 'ana',
				title: 'Ask Ana',
				notice: null,
				rows: [
					row('ai_lolly_logo', `Ask Ana: “${query}”`, null, () =>
						this.askAna(query)
					)
				]
			});
		}

		this.groups = groups;
		this.rows = groups.flatMap(group => group.rows);
		this.setActive(0);
	}
}
